/* Tienda Novale – product page: variant picker, gallery, quantity breaks, gifts, bundles, sticky bar,
   custom fields, clickable discount and delivery dates. */
(function () {
  const TN = (window.TN = window.TN || {});
  if (TN.product) return;

  /* ---------- Helpers ---------- */
  const readJSON = (element) => {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (e) {
      return null;
    }
  };
  const escapeHTML = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => TN.formatMoney(cents);
  const moneyShort = (cents) => TN.formatMoney(cents).replace(/([.,])00(?!\d)/, '');
  const strings = () => window.variantStrings || {};

  /* Same tokens as the price-text snippet. */
  const fillTemplate = (template, values) => {
    let out = template || '';
    if (values.quantity != null) out = out.split('[quantity]').join(values.quantity);
    if (values.name != null) out = out.split('[name]').join(escapeHTML(values.name));
    ['amount_saved_rounded', 'amount_saved', 'compare_price_each', 'price_each', 'compare_price', 'price'].forEach((key) => {
      const token = `[${key}]`;
      if (out.includes(token)) out = out.split(token).join(`<span>${moneyShort(values[key] || 0)}</span>`);
    });
    return out;
  };

  const variants = {
    find(list, options) {
      return (list || []).find((variant) => variant.options.every((value, index) => value === options[index])) || null;
    },
    /* First available variant keeping the changed option and as many of the earlier options as possible. */
    fallback(list, options, changedIndex) {
      let best = null;
      let bestScore = -1;
      (list || []).forEach((variant) => {
        if (!variant.available || variant.options[changedIndex] !== options[changedIndex]) return;
        let score = 0;
        variant.options.forEach((value, index) => {
          if (index !== changedIndex && value === options[index]) score += 2 ** (variant.options.length - index);
        });
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      });
      return best;
    },
  };
  TN.variants = variants;

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = readJSON(this.querySelector('script[data-variants]')) || [];
      this.addEventListener('change', (event) => this.onChange(event));
      this.currentVariant = variants.find(this.variants, this.selectedOptions());
      this.updateAvailability();
    }

    fieldsets() {
      return Array.from(this.querySelectorAll('fieldset[data-option-position]'));
    }

    selectedOptions() {
      return this.fieldsets().map((fieldset) => {
        const radio = fieldset.querySelector('input[type="radio"]:checked');
        if (radio) return radio.value;
        const select = fieldset.querySelector('select[data-option-select]');
        return select ? select.value : null;
      });
    }

    setOptions(options) {
      this.fieldsets().forEach((fieldset, index) => {
        const value = options[index];
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => (input.checked = input.value === value));
        const select = fieldset.querySelector('select[data-option-select]');
        if (select) select.value = value;
      });
    }

    findVariant(options) {
      return variants.find(this.variants, options);
    }

    onChange(event) {
      const fieldset = event.target.closest('fieldset[data-option-position]');
      if (!fieldset) return;
      const changedIndex = this.fieldsets().indexOf(fieldset);
      const options = this.selectedOptions();
      let variant = this.findVariant(options);
      if (this.dataset.skipUnavailable === 'true' && (!variant || !variant.available)) {
        const fallback = variants.fallback(this.variants, options, changedIndex);
        if (fallback) {
          variant = fallback;
          this.setOptions(variant.options);
        }
      }
      this.selectVariant(variant, { fromPicker: true });
    }

    /* Also used by the sticky bar. */
    selectVariant(variant, { fromPicker = false } = {}) {
      if (variant && !fromPicker) this.setOptions(variant.options);
      this.currentVariant = variant;
      this.updateLabels();
      this.updateAvailability();
      TN.product.variantChanged(this, variant);
    }

    updateLabels() {
      const options = this.selectedOptions();
      this.fieldsets().forEach((fieldset, index) => {
        fieldset.querySelectorAll('[data-option-label]').forEach((label) => {
          label.innerHTML = (label.getAttribute('data-template') || '').split('[selected]').join(escapeHTML(options[index] || ''));
        });
      });
    }

    updateAvailability() {
      const options = this.selectedOptions();
      this.fieldsets().forEach((fieldset, index) => {
        const previous = options.slice(0, index);
        const candidates = this.variants.filter((variant) => previous.every((value, i) => variant.options[i] === value));
        const state = (value) => {
          const matching = candidates.filter((variant) => variant.options[index] === value);
          if (!matching.length) return 'missing';
          return matching.some((variant) => variant.available) ? 'available' : 'sold-out';
        };
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const current = state(input.value);
          input.classList.toggle('is-unavailable', current !== 'available');
          input.classList.toggle('is-missing', current === 'missing');
        });
        fieldset.querySelectorAll('select[data-option-select] option').forEach((option) => {
          option.toggleAttribute('data-unavailable', state(option.value) !== 'available');
        });
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product (variant change) ---------- */
  const requests = {};
  TN.product = {
    variantChanged(picker, variant) {
      const sectionId = picker.dataset.section;
      const original = picker.dataset.originalSection || sectionId;

      this.toggleAddButton(sectionId, variant);
      emit('tn:variant-change', { sectionId, variant });
      if (!variant) return;

      this.updateForms(picker, sectionId, variant);
      if (picker.dataset.updateUrl === 'true') window.history.replaceState({}, '', `${picker.dataset.url}?variant=${variant.id}`);
      const share = document.getElementById(`Share-${sectionId}`);
      if (share && typeof share.updateUrl === 'function') share.updateUrl(`${window.shopUrl || ''}${picker.dataset.url}?variant=${variant.id}`);
      document.querySelectorAll(`[id^="MediaGallery-${sectionId}"]`).forEach((gallery) => TN.gallery.onVariant(gallery, variant, picker.dataset.filtering === 'true'));
      this.updatePickup(variant);
      this.refresh(picker, sectionId, original, variant);
    },

    updateForms(picker, sectionId, variant) {
      const forms = document.querySelectorAll(`#${CSS.escape(picker.dataset.form)}, #product-form-installment-${CSS.escape(sectionId)}`);
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },

    toggleAddButton(sectionId, variant) {
      const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const price = document.getElementById(`price-${sectionId}`);
      if (price) price.classList.toggle('visibility-hidden', !variant);
      if (!button) return;
      const label = button.querySelector('.product-form__submit-label');
      const buttonPrice = button.querySelector('.product-form__submit-price');
      if (!variant || !variant.available) {
        button.setAttribute('disabled', 'disabled');
        if (label) label.textContent = variant ? strings().soldOut : strings().unavailable;
        if (buttonPrice) buttonPrice.classList.add('hidden');
      }
    },

    updatePickup(variant) {
      const pickup = document.querySelector('pickup-availability');
      if (!pickup) return;
      if (variant.available && typeof pickup.fetchAvailability === 'function') pickup.fetchAvailability(variant.id);
      else {
        pickup.removeAttribute('available');
        pickup.innerHTML = '';
      }
    },

    /* Fetches the section for the variant and swaps every [data-variant-refresh] element of it. */
    refresh(picker, sectionId, original, variant) {
      const requestId = (requests[sectionId] = (requests[sectionId] || 0) + 1);
      fetch(`${picker.dataset.url}?variant=${variant.id}&section_id=${original}`)
        .then((response) => response.text())
        .then((text) => {
          if (requestId !== requests[sectionId]) return;
          const html = new DOMParser().parseFromString(text, 'text/html');
          document.querySelectorAll('[data-variant-refresh][id]').forEach((element) => {
            if (!element.id.endsWith(sectionId) || element.id.slice(0, -sectionId.length).endsWith('quickadd-')) return;
            const source = html.getElementById(element.id.replace(sectionId, original));
            if (!source) return;
            const fresh = document.importNode(source, true);
            fresh.id = element.id;
            if (element.hasAttribute('aria-haspopup')) fresh.setAttribute('aria-haspopup', element.getAttribute('aria-haspopup'));
            element.replaceWith(fresh);
          });
          emit('tn:variant-refreshed', { sectionId, variant });
          if (typeof publish === 'function') publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: original, html, variant } });
        })
        .catch((error) => console.error(error));
    },
  };

  /* ---------- Gallery ---------- */
  TN.gallery = {
    visibleSlides(gallery) {
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      return viewer ? Array.from(viewer.querySelectorAll('.product__media-item')).filter((slide) => !slide.classList.contains('hidden')) : [];
    },

    onVariant(gallery, variant, filtering) {
      const sectionId = gallery.dataset.section;
      let target = variant.featured_media ? gallery.querySelector(`[data-media-id="${sectionId}-${variant.featured_media.id}"]`) : null;
      if (filtering) {
        this.filter(gallery, variant);
        if (!target || target.classList.contains('hidden')) {
          const first = this.visibleSlides(gallery)[0];
          if (first && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(first.dataset.mediaId, false);
          target = null;
        }
      }
      if (target && typeof gallery.setActiveMedia === 'function') {
        const prepend = gallery.dataset.prepend === 'true' || gallery.dataset.hideVariants === 'true';
        gallery.setActiveMedia(target.dataset.mediaId, prepend);
        if (prepend) {
          const modal = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
          const media = modal && modal.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (media) modal.prepend(media);
        }
      }
      const dots = gallery.querySelector('gallery-dots');
      if (dots) dots.build();
    },

    /* Shows only the media whose alt text is the selected option value (or "always_display"). */
    filter(gallery, variant) {
      const value = variant[gallery.dataset.filterOption];
      const items = gallery.querySelectorAll('.product__media-item[data-alt], .thumbnail-list__item[data-alt]');
      const matches = (element) => element.dataset.alt === value || element.dataset.alt === 'always_display';
      const anyMatch = Array.from(items).some((element) => element.dataset.alt === value);
      items.forEach((element) => element.classList.toggle('hidden', anyMatch && !matches(element)));
      gallery.querySelectorAll('slider-component').forEach((slider) => {
        if (typeof slider.resetPages === 'function') slider.resetPages();
        if (slider.slider) slider.slider.scrollTo({ left: 0 });
      });
    },
  };

  class GalleryDots extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      if (!this.gallery) return;
      this.addEventListener('click', (event) => {
        const dot = event.target.closest('.gallery-dots__dot');
        if (!dot) return;
        const slide = TN.gallery.visibleSlides(this.gallery)[Array.from(this.children).indexOf(dot)];
        if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
      this.build();
    }

    build() {
      const count = TN.gallery.visibleSlides(this.gallery).length;
      const template = this.querySelector('.gallery-dots__dot');
      const inner = template ? template.querySelector('.gallery-dots__dot-inner') : null;
      const color = inner ? inner.className.replace('gallery-dots__dot-inner', '').trim() : '';
      const label = template ? (template.getAttribute('aria-label') || '').replace(/\s*\d+$/, '') : '';
      let html = '';
      for (let i = 0; i < count; i++) {
        html += `<button type="button" class="gallery-dots__dot${i === 0 ? ' is-active' : ''}" aria-label="${escapeHTML(label)} ${i + 1}"><span class="gallery-dots__dot-inner ${color}"></span></button>`;
      }
      this.innerHTML = html;
      this.hidden = count < 2;
    }

    setActive(index) {
      Array.from(this.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }
  }
  if (!customElements.get('gallery-dots')) customElements.define('gallery-dots', GalleryDots);

  /* slideChanged does not bubble: listen in the capture phase. */
  document.addEventListener(
    'slideChanged',
    (event) => {
      const gallery = event.target.closest && event.target.closest('media-gallery');
      if (!gallery || !event.target.id.startsWith('GalleryViewer')) return;
      const current = event.detail && event.detail.currentElement;
      const dots = gallery.querySelector('gallery-dots');
      if (dots && current) dots.setActive(TN.gallery.visibleSlides(gallery).indexOf(current));
      gallery.querySelectorAll('internal-video').forEach((video) => {
        if (video.dataset.autoplay === 'true' || (current && current.contains(video))) return;
        if (typeof video.onSlideHidden === 'function') video.onSlideHidden();
      });
    },
    true
  );

  /* ---------- Quantity breaks ---------- */
  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.variants = readJSON(this.querySelector('script[data-variants]')) || [];
      this.addEventListener('change', (event) => {
        if (event.target.matches('.quantity-breaks__input')) this.onBreakChange();
        else if (event.target.matches('[data-option-index]')) this.onUnitChange(event.target);
      });
      this.onVariantChange = (event) => {
        if (event.detail.sectionId !== this.sectionId || !event.detail.variant) return;
        this.variant = event.detail.variant;
        this.querySelectorAll('.quantity-break__unit select[data-option-index]').forEach((select) => {
          select.value = this.variant.options[Number(select.dataset.optionIndex)];
        });
        this.updatePrices();
      };
      document.addEventListener('tn:variant-change', this.onVariantChange);
      this.syncQuantityInput();
      this.dispatchQuantity();
    }

    disconnectedCallback() {
      document.removeEventListener('tn:variant-change', this.onVariantChange);
    }

    get checked() {
      return this.querySelector('.quantity-breaks__input:checked');
    }

    get quantity() {
      return this.checked ? parseInt(this.checked.value, 10) || 1 : 1;
    }

    get mainVariant() {
      if (this.variant) return this.variant;
      const form = document.getElementById(this.dataset.form);
      const input = form && form.querySelector('input[name="id"]');
      return (input && this.variants.find((variant) => String(variant.id) === input.value)) || this.variants[0];
    }

    onBreakChange() {
      this.syncQuantityInput();
      this.dispatchQuantity();
    }

    /* Keeps a quantity field in the main form (read by apps such as COD forms). */
    syncQuantityInput() {
      const form = document.getElementById(this.dataset.form);
      if (!form) return;
      let input = form.querySelector('input[name="quantity"][data-breaks]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.dataset.breaks = '';
        form.appendChild(input);
      }
      input.value = this.quantity;
    }

    dispatchQuantity() {
      emit('tn:quantity-change', { sectionId: this.sectionId, quantity: this.quantity });
    }

    unitVariant(unit) {
      const options = Array.from(unit.querySelectorAll('select[data-option-index]')).map((select) => select.value);
      return variants.find(this.variants, options);
    }

    onUnitChange(select) {
      const unit = select.closest('.quantity-break__unit');
      if (unit && this.dataset.skipUnavailable === 'true') {
        const selects = Array.from(unit.querySelectorAll('select[data-option-index]'));
        const options = selects.map((s) => s.value);
        const variant = variants.find(this.variants, options);
        if (!variant || !variant.available) {
          const fallback = variants.fallback(this.variants, options, Number(select.dataset.optionIndex));
          if (fallback) selects.forEach((s) => (s.value = fallback.options[Number(s.dataset.optionIndex)]));
        }
      }
      this.updatePrices();
    }

    /* Variant id of each unit of the selected option, or null when it has no unit pickers. */
    unitVariantIds() {
      const input = this.checked;
      const label = input && this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const units = label ? Array.from(label.querySelectorAll('.quantity-break__unit')) : [];
      if (!units.length) return null;
      const ids = [];
      for (const unit of units) {
        const variant = this.unitVariant(unit);
        if (!variant) return null;
        ids.push(variant.id);
      }
      return ids;
    }

    updatePrices() {
      if (this.dataset.updatePrices !== 'true') return;
      const main = this.mainVariant;
      if (!main) return;
      this.querySelectorAll('.quantity-breaks__input').forEach((input) => {
        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        const quantity = parseInt(input.value, 10) || 1;
        const keep = parseFloat(input.dataset.keep || '1');
        const fixed = parseFloat(input.dataset.fixed || '0');
        const units = Array.from(label.querySelectorAll('.quantity-break__unit'));
        const list = units.length ? units.map((unit) => this.unitVariant(unit) || main) : Array(quantity).fill(main);
        let base = 0;
        let compareBase = 0;
        list.forEach((variant) => {
          base += variant.price;
          compareBase += input.dataset.compareMode === 'compare_price' && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
        });
        const total = Math.max(0, Math.round(base * keep - fixed));
        const saved = Math.max(0, compareBase - total);
        const values = {
          quantity,
          price: total,
          compare_price: compareBase,
          amount_saved: saved,
          amount_saved_rounded: Math.round(saved / 100) * 100,
          price_each: Math.floor(total / quantity),
          compare_price_each: Math.floor(compareBase / quantity),
        };
        label.querySelectorAll('[data-price-template]').forEach((element) => {
          element.innerHTML = fillTemplate(element.getAttribute('data-price-template'), values);
        });
        const caption = label.querySelector('.quantity-break__caption');
        if (caption) caption.classList.toggle('hidden', saved <= 0 && (caption.getAttribute('data-price-template') || '').includes('[amount_saved'));
        const compare = label.querySelector('.quantity-break__compare-price');
        if (compare) compare.classList.toggle('hidden', compareBase <= total);
      });
    }
  }
  if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);

  /* ---------- Quantity gifts ---------- */
  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onQuantity = (event) => {
        if (event.detail.sectionId === this.sectionId) this.update(event.detail.quantity);
      };
      this.onInput = (event) => {
        if (event.target.id === `Quantity-${this.sectionId}`) this.update(parseInt(event.target.value, 10) || 1);
      };
      document.addEventListener('tn:quantity-change', this.onQuantity);
      document.addEventListener('change', this.onInput);
      document.addEventListener('input', this.onInput);

      const breaks = document.getElementById(`quantity-breaks-${this.sectionId}`);
      const input = document.getElementById(`Quantity-${this.sectionId}`);
      this.update(breaks && breaks.quantity ? breaks.quantity : input ? parseInt(input.value, 10) || 1 : 1);
    }

    disconnectedCallback() {
      document.removeEventListener('tn:quantity-change', this.onQuantity);
      document.removeEventListener('change', this.onInput);
      document.removeEventListener('input', this.onInput);
    }

    update(quantity) {
      this.querySelectorAll('.quantity-gift[data-quantity]').forEach((gift) => {
        const unlocked = quantity >= (parseInt(gift.dataset.quantity, 10) || 1);
        gift.dataset.unlocked = unlocked ? 'true' : 'false';
        gift.classList.toggle('is-unlocked', unlocked);
      });
    }
  }
  if (!customElements.get('quantity-gifts')) customElements.define('quantity-gifts', QuantityGifts);

  /* ---------- Bundle offer ---------- */
  class TnBundle extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.items = Array.from(this.querySelectorAll('.bundle-offer__item'));
      this.items.forEach((item) => {
        item.variants = readJSON(item.querySelector('.upsell__variants script[type="application/json"]'));
        item.dataset.available = 'true';
      });
      this.addEventListener('change', (event) => {
        const item = event.target.closest('.bundle-offer__item');
        if (item && event.target.matches('[data-option-index]')) this.onItemChange(item, event.target);
      });
      this.onVariantChange = (event) => {
        if (event.detail.sectionId !== this.sectionId || !event.detail.variant) return;
        const variant = event.detail.variant;
        this.items.forEach((item) => {
          if (item.hasAttribute('data-main-product') && !item.variants) {
            this.setItemVariant(item, variant, variant.featured_image ? variant.featured_image.src : null);
          }
        });
      };
      document.addEventListener('tn:variant-change', this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('tn:variant-change', this.onVariantChange);
    }

    onItemChange(item, select) {
      const selects = Array.from(item.querySelectorAll('select[data-option-index]'));
      const options = selects.map((s) => s.value);
      let variant = variants.find(item.variants, options);
      if ((!variant || !variant.available) && this.dataset.skipUnavailable === 'true') {
        const fallback = variants.fallback(item.variants, options, Number(select.dataset.optionIndex));
        if (fallback) {
          variant = fallback;
          selects.forEach((s) => (s.value = variant.options[Number(s.dataset.optionIndex)]));
        }
      }
      if (variant) this.setItemVariant(item, variant, variant.image);
      else {
        item.dataset.available = 'false';
        this.updateButton();
      }
    }

    setItemVariant(item, variant, imageUrl) {
      const keep = parseFloat(item.dataset.keep || '1');
      const fixed = parseFloat(item.dataset.fixed || '0');
      const price = Math.max(0, Math.round(variant.price * keep - fixed));
      const compare = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
      item.dataset.variantId = variant.id;
      item.dataset.price = price;
      item.dataset.compare = compare;
      item.dataset.available = variant.available ? 'true' : 'false';

      if (this.dataset.updatePrices === 'true') {
        item.querySelectorAll('.bundle-offer__price').forEach((element) => {
          element.textContent = price === 0 && this.dataset.freeText ? this.dataset.freeText : money(price);
        });
        item.querySelectorAll('.bundle-offer__compare').forEach((element) => {
          element.textContent = money(compare);
          element.classList.toggle('hidden', !(compare > price));
        });
        this.updateTotals();
      }
      if (imageUrl && this.dataset.variantImage === 'true') {
        const image = item.querySelector('.bundle-offer__image');
        if (image) {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        }
      }
      this.updateButton();
    }

    updateTotals() {
      let sum = 0;
      let compare = 0;
      this.items.forEach((item) => {
        sum += Number(item.dataset.price) || 0;
        compare += Number(item.dataset.compare) || 0;
      });
      const total = Math.max(0, Math.round(sum * parseFloat(this.dataset.keep || '1') - parseFloat(this.dataset.fixed || '0')));
      const totalEl = this.querySelector('.bundle-offer__total');
      const compareEl = this.querySelector('.bundle-offer__total-compare');
      if (totalEl) totalEl.textContent = money(total);
      if (compareEl) {
        compareEl.textContent = money(compare);
        compareEl.classList.toggle('hidden', !(compare > total));
      }
    }

    updateButton() {
      const available = this.items.every((item) => item.dataset.available !== 'false');
      const form = document.querySelector(`product-form[data-bundle="#${CSS.escape(this.id)}"]`);
      const button = form && form.querySelector('button[name="add"]');
      if (button) button.toggleAttribute('disabled', !available);
    }
  }
  if (!customElements.get('tn-bundle')) customElements.define('tn-bundle', TnBundle);

  /* ---------- Custom product fields ---------- */
  TN.fields = {
    validate(scope, show) {
      let valid = true;
      (scope || document).querySelectorAll('tn-custom-field').forEach((field) => {
        if (field.valid) return;
        valid = false;
        if (show) field.showError(true);
      });
      if (!valid && show) {
        const first = (scope || document).querySelector('tn-custom-field.has-error');
        if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return valid;
    },

    /* Disables the add to cart buttons of a section while a required field is empty. */
    refresh(sectionId) {
      const fields = Array.from(document.querySelectorAll(`tn-custom-field[data-section="${CSS.escape(sectionId)}"]`));
      const invalid = fields.filter((field) => !field.valid);
      const message = invalid.length ? invalid[invalid.length - 1].dataset.atcError || '' : '';
      [document.getElementById(`ProductSubmitButton-${sectionId}`), document.getElementById(`SecondaryAtc-${sectionId}`)].forEach((button) => {
        if (!button) return;
        button.classList.toggle('has-field-error', invalid.length > 0);
        if (button.id.startsWith('ProductSubmitButton')) {
          let error = button.querySelector('.product-form__submit-error');
          if (!error && message) {
            error = document.createElement('span');
            error.className = 'product-form__submit-error';
            (button.querySelector('.button__label') || button).appendChild(error);
          }
          if (error) error.textContent = message;
        }
        if (invalid.length) button.setAttribute('aria-disabled', 'true');
        else if (!button.classList.contains('loading')) button.removeAttribute('aria-disabled');
      });
      const sticky = document.getElementById(`StickyAtc-${sectionId}`);
      if (sticky && typeof sticky.setFieldError === 'function') {
        const showInSticky = invalid.some((field) => field.dataset.stickyError === 'true');
        sticky.setFieldError(invalid.length > 0, showInSticky ? message : '');
      }
    },
  };

  class TnCustomField extends HTMLElement {
    connectedCallback() {
      this.touched = false;
      this.errorElement = this.querySelector('.custom-field__error');
      const onChange = () => {
        if (this.value) this.touched = true;
        this.showError(this.touched && !this.valid);
        TN.fields.refresh(this.dataset.section);
      };
      this.addEventListener('input', onChange);
      this.addEventListener('change', onChange);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => TN.fields.refresh(this.dataset.section));
      else setTimeout(() => TN.fields.refresh(this.dataset.section));
    }

    get value() {
      if (this.dataset.type === 'pills') {
        const checked = this.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : '';
      }
      const input = this.querySelector('.custom-field__input');
      return input ? input.value.trim() : '';
    }

    get valid() {
      const value = this.value;
      if (!value) return this.dataset.required !== 'true';
      const input = this.querySelector('input.custom-field__input:not([type="radio"])');
      return input ? input.checkValidity() : true;
    }

    showError(show) {
      this.classList.toggle('has-error', show);
      if (this.errorElement) this.errorElement.classList.toggle('hidden', !show);
    }
  }
  if (!customElements.get('tn-custom-field')) customElements.define('tn-custom-field', TnCustomField);

  document.addEventListener('tn:variant-refreshed', (event) => TN.fields.refresh(event.detail.sectionId));

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.button = this.querySelector('.sticky-atc__submit');
      this.label = this.querySelector('.sticky-atc__label');
      this.error = this.querySelector('.sticky-atc__error');
      if (!this.button) return;
      this.labelHTML = this.label && this.label.querySelector('.sticky-atc__label-text') ? this.label.innerHTML : null;

      this.button.addEventListener('click', () => this.onClick());
      const combined = this.querySelector('[data-sticky-combined]');
      if (combined) combined.addEventListener('change', () => this.onCombinedChange(combined));
      this.querySelectorAll('[data-sticky-option]').forEach((select) => select.addEventListener('change', () => this.onOptionChange(select)));

      this.onVariantChange = (event) => {
        if (event.detail.sectionId === this.sectionId) this.sync(event.detail.variant);
      };
      document.addEventListener('tn:variant-change', this.onVariantChange);

      if (this.dataset.afterScroll === 'true') this.observe();
      this.resizeObserver = new ResizeObserver(() => this.updateOffset());
      this.resizeObserver.observe(this);
      this.updateOffset();
    }

    disconnectedCallback() {
      document.removeEventListener('tn:variant-change', this.onVariantChange);
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.body.style.removeProperty('--sticky-atc-offset');
    }

    get picker() {
      return document.getElementById(`variant-selects-${this.sectionId}`);
    }

    updateOffset() {
      const visible = !this.classList.contains('is-hidden');
      document.body.style.setProperty('--sticky-atc-offset', visible ? `${this.offsetHeight}px` : '0px');
    }

    /* Shown once the main buy area has been scrolled past. */
    observe() {
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      const target =
        section.querySelector('.product-buy-buttons:not(.hidden)') ||
        section.querySelector('.product__app-block') ||
        section.querySelector('variant-picker') ||
        document.getElementById(`ProductInfo-${this.sectionId}`);
      if (!target) {
        this.classList.remove('is-hidden');
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-hidden', !past);
          this.updateOffset();
        });
      });
      this.observer.observe(target);
    }

    onCombinedChange(select) {
      const picker = this.picker;
      const variant = picker && picker.variants.find((v) => String(v.id) === select.value);
      if (variant) {
        picker.selectVariant(variant);
        return;
      }
      const form = document.getElementById(this.dataset.form);
      const input = form && form.querySelector('input[name="id"]');
      if (input) input.value = select.value;
    }

    onOptionChange(select) {
      const picker = this.picker;
      if (!picker) return;
      const index = Number(select.dataset.stickyOption);
      const options = picker.selectedOptions();
      options[index] = select.value;
      let variant = picker.findVariant(options);
      if ((!variant || !variant.available) && picker.dataset.skipUnavailable === 'true') {
        variant = variants.fallback(picker.variants, options, index) || variant;
      }
      if (variant) picker.selectVariant(variant);
      else {
        picker.setOptions(options);
        picker.selectVariant(null, { fromPicker: true });
      }
    }

    sync(variant) {
      const combined = this.querySelector('[data-sticky-combined]');
      if (combined && variant) combined.value = variant.id;
      this.querySelectorAll('[data-sticky-option]').forEach((select) => {
        if (variant) select.value = variant.options[Number(select.dataset.stickyOption)];
      });
      const image = document.getElementById(`StickyAtcImage-${this.sectionId}`);
      if (image && variant && variant.featured_image) {
        const src = variant.featured_image.src;
        image.removeAttribute('srcset');
        image.src = `${src}${src.includes('?') ? '&' : '?'}width=200`;
      }
      if (this.dataset.scrollTarget || !this.label) return;
      if (!variant || !variant.available) {
        this.button.setAttribute('disabled', 'disabled');
        const soldOut = this.querySelector('template[data-sold-out]');
        this.label.textContent = variant ? (soldOut ? soldOut.innerHTML.trim() : strings().soldOut) : strings().unavailable;
        return;
      }
      this.button.removeAttribute('disabled');
      this.label.innerHTML = this.labelHTML || `<span class="sticky-atc__label-text">${escapeHTML(this.dataset.label || strings().addToCart || '')}</span>`;
      const price = this.label.querySelector('.sticky-atc__button-price');
      if (price) price.textContent = `${money(variant.price)} • `;
    }

    async onClick() {
      if (this.dataset.scrollTarget) {
        const target = document.querySelector(this.dataset.scrollTarget);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const section = document.getElementById(`shopify-section-${this.sectionId}`) || document;
      if (this.fieldError) {
        TN.fields.validate(section, true);
        return;
      }
      const form =
        document.getElementById(`ProductForm-${this.sectionId}`) ||
        document.querySelector(`product-form[data-section="${CSS.escape(this.sectionId)}"][data-main="true"]`);
      if (!form || typeof form.onSubmitHandler !== 'function') return;
      await form.onSubmitHandler(null, { button: this.button });
    }

    setFieldError(invalid, message) {
      this.fieldError = invalid;
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.classList.toggle('hidden', !(invalid && message));
      this.button.classList.toggle('has-field-error', invalid && !!message);
    }

    flashError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.classList.remove('hidden');
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => {
        if (!this.fieldError) this.error.classList.add('hidden');
      }, 3000);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Clickable discount ---------- */
  const DISCOUNT_KEY = 'tn-applied-discount';
  class TnClickableDiscount extends HTMLElement {
    connectedCallback() {
      this.code = (this.dataset.code || '').trim();
      this.button = this.querySelector('.clickable-discount__button');
      this.error = this.querySelector('.clickable-discount__error');
      if (!this.code || !this.button) return;
      let applied = null;
      try {
        applied = localStorage.getItem(DISCOUNT_KEY);
      } catch (e) {}
      this.setApplied(applied === this.code);
      this.button.addEventListener('click', () => this.apply());
    }

    async apply() {
      if (this.classList.contains('is-applied') || this.classList.contains('is-loading')) return;
      this.classList.add('is-loading');
      if (this.error) this.error.classList.add('hidden');
      try {
        const response = await fetch(`/discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent('/cart.js')}`, {
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(String(response.status));
        try {
          localStorage.setItem(DISCOUNT_KEY, this.code);
        } catch (e) {}
        this.setApplied(true);
      } catch (e) {
        if (this.error) this.error.classList.remove('hidden');
      } finally {
        this.classList.remove('is-loading');
      }
    }

    setApplied(applied) {
      this.classList.toggle('is-applied', applied);
      this.button.setAttribute('aria-pressed', applied ? 'true' : 'false');
    }
  }
  if (!customElements.get('tn-clickable-discount')) customElements.define('tn-clickable-discount', TnClickableDiscount);

  /* ---------- Delivery dates ---------- */
  class TnDates extends HTMLElement {
    connectedCallback() {
      const split = (text) => (text || '').split(',').map((part) => part.trim());
      this.days = split(this.dataset.days); /* Monday first */
      this.months = split(this.dataset.months);
      this.format = this.dataset.format || 'day_mm_dd';
      this.querySelectorAll('[data-date-text]').forEach((element) => {
        const text = element.getAttribute('data-date-text') || '';
        const start = this.formatDate(this.addDays(parseInt(element.dataset.min, 10) || 0));
        const end = this.formatDate(this.addDays(parseInt(element.dataset.max, 10) || 0));
        element.innerHTML = text.split('[start_date]').join(escapeHTML(start)).split('[end_date]').join(escapeHTML(end));
      });
      this.classList.add('is-ready');
    }

    addDays(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date;
    }

    ordinal(day) {
      if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
      return `${day}${{ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'}`;
    }

    formatDate(date) {
      const day = date.getDate();
      const weekday = this.days[(date.getDay() + 6) % 7] || '';
      const month = this.months[date.getMonth()] || '';
      const dd = String(day).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      switch (this.format) {
        case 'day_dd_mm':
          return `${weekday}, ${day}. ${month}`;
        case 'mm_dd':
          return `${month} ${this.ordinal(day)}`;
        case 'dd_mm':
          return `${day}. ${month}`;
        case 'dd_mm_no_dot':
          return `${day} ${month}`;
        case 'day_dd_mm_numeric':
          return `${weekday}, ${dd}. ${mm}.`;
        case 'dd_mm_numeric':
          return `${dd}. ${mm}.`;
        default:
          return `${weekday}, ${month} ${this.ordinal(day)}`;
      }
    }
  }
  if (!customElements.get('tn-dates')) customElements.define('tn-dates', TnDates);
})();
